// routeSegmentMap.ts
// ==================
//
// Wave 14ca-fix64: Konvertiert die `routeSegments`-Datenstruktur einer
// CustomTour (Pin-Route-Response, persistiert beim Save) in eine Form
// die direkt vom ElevationProfile + Trail-Badge-Listen genutzt werden
// kann.
//
// Eingabe-Datenstruktur:
//   - routeSegments: [{kind, trailId, from, to}]
//     `from`/`to` sind Indices in das `coords`-Array.
//   - coords:  [[lon, lat], ...]  — die volle Route-Polyline.
//   - trails:  CachedTrail[] — fuer Difficulty-Farbe + Trail-Name-Lookup.
//
// Ausgabe:
//   - ElevationSegment[]: jedes Element kennt seinen Range in METERN
//     entlang der Route + die Farbe + die Trail-Info.
//
// Warum Meter und nicht Sample-Index?
//   ElevationProfile rendert auf Basis von `data.distances` (cumulative
//   meters pro Sample). Wir geben dem Profil einfach Meter-Ranges, es
//   findet selbst raus welche Samples dazu gehoeren — kein Coupling an
//   Sample-Count, robust gegen Resampling.

import { haversineMeters } from './rideRecorder';
import type { CachedTrail } from './trails';
import type { Difficulty } from '@adtu/shared';
import { DIFFICULTY_COLOR } from '@adtu/shared';

export interface ElevationSegment {
  /** Start- und End-Position des Segments in Metern entlang der Route. */
  fromMeters: number;
  toMeters: number;
  /** Hex-Farbe der Polyline-Linie fuer dieses Segment. */
  color: string;
  /** Trail-ID falls Segment auf einem bekannten Trail liegt, sonst null. */
  trailId: string | null;
  /** Anzeigename des Trails (oder 'Connector' / 'Bridge' fuer Non-Trail-
   *  Segmente). */
  trailName: string;
  kind: 'trail' | 'connector' | 'bridge';
  /** Difficulty des zugehoerigen Trails — fuer Trail-Badge-Faerbung im UI. */
  difficulty?: Difficulty;
}

/**
 * Wave 14ca-fix64: Default-Farben fuer Non-Trail-Segmente.
 *   Connector  → grau    (OSM-Verbindung zwischen Trails)
 *   Bridge     → grau gestrichelt (kuenstliche Routing-Luecke; visuell
 *                 koennten wir spaeter dashed implementieren)
 */
const COLOR_CONNECTOR = '#6b7280'; // gray-500
const COLOR_BRIDGE = '#9ca3af'; // gray-400

export function routeSegmentsToElevationSegments(
  routeSegments: ReadonlyArray<{
    kind: 'trail' | 'connector' | 'bridge';
    trailId: string | null;
    from: number;
    to: number;
  }>,
  coords: ReadonlyArray<[number, number]>,
  trails: ReadonlyArray<CachedTrail>,
  /** Wave 14ca-fix64-v3: Whitelist der Trail-IDs die der User beim
   *  Planen explizit ausgewaehlt hat. Wenn gesetzt, werden Segmente mit
   *  einer trailId AUSSERHALB dieser Liste auf 'connector' demoviert
   *  (grau, kein Badge). User-Wunsch: "ich will nur Trails sehen die
   *  ich beim Planen ausgewaehlt habe — die anderen liegen nur zufaellig
   *  nahe der Route". null/undefined = keine Filterung. */
  allowedTrailIds?: ReadonlyArray<string> | null,
): ElevationSegment[] {
  if (routeSegments.length === 0 || coords.length < 2) return [];
  const allowSet =
    allowedTrailIds && allowedTrailIds.length > 0
      ? new Set(allowedTrailIds)
      : null;
  // Cumulative distance pro coord. Index i = Distanz von coords[0] bis coords[i].
  const cum: number[] = new Array(coords.length);
  cum[0] = 0;
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1]!;
    const b = coords[i]!;
    cum[i] = cum[i - 1]! + haversineMeters(a[1], a[0], b[1], b[0]);
  }

  // Trails-Lookup-Map fuer O(1) name + difficulty.
  const trailMap = new Map<string, CachedTrail>();
  for (const t of trails) trailMap.set(t.id, t);

  const out: ElevationSegment[] = [];
  for (const seg of routeSegments) {
    const fromIdx = Math.max(0, Math.min(coords.length - 1, seg.from));
    const toIdx = Math.max(0, Math.min(coords.length - 1, seg.to));
    if (toIdx <= fromIdx) continue;
    const fromMeters = cum[fromIdx]!;
    const toMeters = cum[toIdx]!;
    if (toMeters <= fromMeters) continue;

    let color: string;
    let trailName: string;
    let difficulty: Difficulty | undefined;
    let effectiveKind: 'trail' | 'connector' | 'bridge' = seg.kind;
    let effectiveTrailId: string | null = seg.trailId;

    // Wave 14ca-fix64-v3: Whitelist-Check — wenn der User eine Trail-
    // Auswahl getroffen hat, alle anderen Trails als Connector
    // demoviert. Damit verschwinden zufaellig-nahe Trails aus der
    // Liste + dem Profil.
    if (
      seg.kind === 'trail' &&
      seg.trailId &&
      allowSet &&
      !allowSet.has(seg.trailId)
    ) {
      effectiveKind = 'connector';
      effectiveTrailId = null;
    }

    if (effectiveKind === 'trail' && effectiveTrailId) {
      const t = trailMap.get(effectiveTrailId);
      if (t) {
        color = t.colorHex || DIFFICULTY_COLOR[t.difficulty];
        trailName = t.name;
        difficulty = t.difficulty;
      } else {
        color = COLOR_CONNECTOR;
        trailName = effectiveTrailId;
      }
    } else if (effectiveKind === 'bridge') {
      color = COLOR_BRIDGE;
      trailName = 'Bridge';
    } else {
      color = COLOR_CONNECTOR;
      trailName = 'Connector';
    }

    out.push({
      fromMeters,
      toMeters,
      color,
      trailId: effectiveTrailId,
      trailName,
      kind: effectiveKind,
      difficulty,
    });
  }
  return out;
}

/**
 * Wave 14ca-fix64: Runtime-Fallback fuer Custom-Tours die VOR dem
 * routeSegments-Save-Patch gespeichert wurden.
 *
 * Die Funktion derived RouteSegments live aus dem Coords-Array, indem
 * jeder Coord-Index auf den naechstgelegenen Trail gesnapped wird (innerhalb
 * `maxOffTrackM`). Konsekutive Coords mit demselben Snap-Trail werden zu
 * 'trail'-Segmenten gruppiert; Stretches ohne Snap zu 'connector'-Segmenten.
 *
 * Performance-Hinweis: O(N_coords × N_trails). Bei einem Liguria-Set von
 * 28 Trails × 2k Custom-Coords = ~56k Operationen, ~50-100 ms. OK fuer
 * einen einmaligen Memo-Compute beim Oeffnen einer Tour.
 *
 * Ergebnis-Format ist exakt CustomTrailRouteSegment[]: gleiche Struktur
 * wie das was Pin-Route-Response liefert + im IDB persistiert.
 */
export function deriveRouteSegments(
  coords: ReadonlyArray<[number, number]>,
  trails: ReadonlyArray<CachedTrail>,
  maxOffTrackM: number = 25,
): Array<{
  kind: 'trail' | 'connector' | 'bridge';
  trailId: string | null;
  from: number;
  to: number;
}> {
  if (coords.length < 2 || trails.length === 0) return [];

  // Quick-Compute Bbox pro Trail damit wir die fern-liegenden schnell
  // skippen koennen (ohne den teuren nearestPointOnTrack zu laufen).
  const trailBboxes = trails.map((t) => {
    const pts = t.track?.points;
    if (!pts || pts.length < 2) {
      return null;
    }
    let minLat = pts[0]!.lat;
    let maxLat = pts[0]!.lat;
    let minLon = pts[0]!.lon;
    let maxLon = pts[0]!.lon;
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i]!;
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lon < minLon) minLon = p.lon;
      if (p.lon > maxLon) maxLon = p.lon;
    }
    return { trail: t, minLat, maxLat, minLon, maxLon };
  });

  // Per-coord Snap → trail-id (oder null).
  const snapped: Array<string | null> = new Array(coords.length);
  for (let i = 0; i < coords.length; i++) {
    const [lon, lat] = coords[i]!;
    const latPad = maxOffTrackM / 111000;
    const lonPad =
      maxOffTrackM / (111000 * Math.cos((lat * Math.PI) / 180));
    let bestId: string | null = null;
    let bestDist = Infinity;
    for (const bb of trailBboxes) {
      if (!bb) continue;
      if (
        lat < bb.minLat - latPad ||
        lat > bb.maxLat + latPad ||
        lon < bb.minLon - lonPad ||
        lon > bb.maxLon + lonPad
      ) {
        continue;
      }
      // O(M) — nearest-point-on-segment-loop. Wir importieren das nicht
      // direkt um Zyklen zu vermeiden; rechnen inline mit equirect-Faktor.
      const pts = bb.trail.track.points;
      const mPerDegLat = 111320;
      const mPerDegLon = 111320 * Math.cos((lat * Math.PI) / 180);
      for (let j = 0; j < pts.length - 1; j++) {
        const a = pts[j]!;
        const b = pts[j + 1]!;
        const ax = a.lon * mPerDegLon;
        const ay = a.lat * mPerDegLat;
        const bx = b.lon * mPerDegLon;
        const by = b.lat * mPerDegLat;
        const px = lon * mPerDegLon;
        const py = lat * mPerDegLat;
        const dx = bx - ax;
        const dy = by - ay;
        const lenSq = dx * dx + dy * dy;
        let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
        if (t < 0) t = 0;
        if (t > 1) t = 1;
        const cx = ax + t * dx;
        const cy = ay + t * dy;
        const d = Math.hypot(px - cx, py - cy);
        if (d < bestDist && d <= maxOffTrackM) {
          bestDist = d;
          bestId = bb.trail.id;
        }
      }
    }
    snapped[i] = bestId;
  }

  // Konsekutive Snaps zu Segmenten gruppieren.
  const out: Array<{
    kind: 'trail' | 'connector' | 'bridge';
    trailId: string | null;
    from: number;
    to: number;
  }> = [];
  let runStart = 0;
  let runId: string | null = snapped[0]!;
  for (let i = 1; i < snapped.length; i++) {
    if (snapped[i] !== runId) {
      out.push({
        kind: runId ? 'trail' : 'connector',
        trailId: runId,
        from: runStart,
        to: i, // exclusive end → fits Pin-Route-Konvention
      });
      runStart = i;
      runId = snapped[i]!;
    }
  }
  // Letztes Segment.
  out.push({
    kind: runId ? 'trail' : 'connector',
    trailId: runId,
    from: runStart,
    to: snapped.length - 1,
  });

  return out;
}

/**
 * Findet das Segment unter einer bestimmten Position (in Metern entlang
 * der Route) — fuer Scrub-Reveal: User scrubbt das Profil, wir zeigen
 * den Trail-Namen an dieser Position.
 */
export function findSegmentAt(
  segments: ReadonlyArray<ElevationSegment>,
  meters: number,
): ElevationSegment | null {
  if (!Number.isFinite(meters)) return null;
  for (const s of segments) {
    if (meters >= s.fromMeters && meters <= s.toMeters) return s;
  }
  return null;
}

/**
 * Reduziert auf einzigartige Trails (fuer Trail-Badge-Listen).
 * Reihenfolge: erste Auftretens entlang der Route.
 */
export function uniqueTrailsFromSegments(
  segments: ReadonlyArray<ElevationSegment>,
): Array<{
  trailId: string;
  trailName: string;
  color: string;
  difficulty?: Difficulty;
  totalMeters: number;
}> {
  const seen = new Map<
    string,
    {
      trailId: string;
      trailName: string;
      color: string;
      difficulty?: Difficulty;
      totalMeters: number;
    }
  >();
  for (const s of segments) {
    if (s.kind !== 'trail' || !s.trailId) continue;
    const existing = seen.get(s.trailId);
    const length = s.toMeters - s.fromMeters;
    if (existing) {
      existing.totalMeters += length;
    } else {
      seen.set(s.trailId, {
        trailId: s.trailId,
        trailName: s.trailName,
        color: s.color,
        difficulty: s.difficulty,
        totalMeters: length,
      });
    }
  }
  return [...seen.values()];
}
